import { CONCSTT, DELIMITER, UI_ONLY_PARAMS } from "./constants";
import { getSectionUrlKeys } from "./api-url";
import type { FilterState } from "./types";

/**
 * Parse CONCSTT-delimited multi-value string.
 * Handles both CONCSTT (2 T's) and CONCSTTT (3 T's) from the API.
 */
export function parseConcsttt(value: string | null): string[] {
  if (!value) return [];
  return value
    .split(DELIMITER)
    .map((v) => v.replace(/CONCST{2,3}$/, ""))
    .filter(Boolean);
}

/**
 * Serialize values with CONCSTT suffix, joined by ---
 */
export function serializeConcsttt(values: string[]): string {
  if (values.length === 0) return "";
  return values.map((v) => v + CONCSTT).join(DELIMITER);
}

/**
 * Parse ---delimited string into array
 */
export function parseDelimited(value: string | null): string[] {
  if (!value) return [];
  return value.split(DELIMITER).filter(Boolean);
}

/**
 * Parse URL searchParams into FilterState
 */
export function parseFilters(searchParams: URLSearchParams): FilterState {
  return {
    refine_search: parseDelimited(searchParams.get("refine_search")),
    availability: searchParams.get("availability") === "TrueCONCSTT",
    brand_name: parseConcsttt(searchParams.get("brand_name")),
    cat_name: searchParams.get("cat_name"),
    subcat_name: searchParams.get("subcat_name"),
    part_name: searchParams.get("part_name"),
    stores: parseConcsttt(searchParams.get("stores")),
    ratings_ranges: searchParams.get("ratings_ranges"),
    reviews_ranges: searchParams.get("reviews_ranges"),
    price_ranges: searchParams.get("price_ranges"),
    price_type: searchParams.get("price_type"),
    vehicle_key: searchParams.get("vehicle_key"),
    enabled_filters: parseDelimited(searchParams.get("enabled_filters")),
    enabled_ranges: parseDelimited(searchParams.get("enabled_ranges")),
    FilterNav: searchParams.get("FilterNav"),
    expanded: searchParams.get("expanded"),
  };
}

/** Filter param keys that need enabled_filters */
const FILTER_KEYS = new Set([
  "availability", "brand_name", "stores",
  "cat_name", "subcat_name", "part_name", "vehicle_key",
]);
/** Range param keys that need enabled_ranges */
const RANGE_KEYS = new Set([
  "ratings_ranges", "reviews_ranges", "price_ranges",
]);
/** Params that are NOT filter/range keys */
const NON_FILTER_PARAMS = new Set([
  "FilterNav", "expanded", "enabled_filters", "enabled_ranges",
  "hitsSize", "from", "refine_search", "search", "price_type",
]);

/**
 * Build a URL string for /products from filter state changes.
 * Computes enabled_filters / enabled_ranges from which filter params
 * have values, so the URL is always a complete, shareable state.
 */
export function buildSearchUrl(
  currentParams: URLSearchParams,
  changes: Partial<Record<string, string | null>>
): string {
  const params = new URLSearchParams(currentParams.toString());

  // Apply changes
  for (const [key, value] of Object.entries(changes)) {
    if (value === null || value === undefined || value === "") {
      params.delete(key);
    } else {
      params.set(key, value);
    }
  }

  // Strip API plumbing params
  params.delete("hitsSize");
  params.delete("from");

  // 1. Start with existing enabled_filters/enabled_ranges from URL (preserve previous state)
  const ef = new Set(
    params.get("enabled_filters")?.split(DELIMITER).filter(Boolean) ?? []
  );
  const er = new Set(
    params.get("enabled_ranges")?.split(DELIMITER).filter(Boolean) ?? []
  );

  // 2. Remove keys whose filter was explicitly cleared in this change
  for (const [key, value] of Object.entries(changes)) {
    if (value === null || value === undefined || value === "") {
      if (FILTER_KEYS.has(key)) ef.delete(key);
      else if (RANGE_KEYS.has(key)) er.delete(key);
      else if (!NON_FILTER_PARAMS.has(key)) ef.delete(key); // attribute group key
    }
  }

  // 3. Add keys for currently applied filter/range params
  params.forEach((_val, key) => {
    if (FILTER_KEYS.has(key)) ef.add(key);
    else if (RANGE_KEYS.has(key)) er.add(key);
    else if (!NON_FILTER_PARAMS.has(key)) ef.add(key); // attribute group
  });

  // 4. Add section browse keys (Level-1 only) for the current tab
  const filterNav = params.get("FilterNav");
  if (filterNav) {
    const sectionKeys = getSectionUrlKeys(filterNav);
    if (sectionKeys?.filters) for (const f of sectionKeys.filters) ef.add(f);
    if (sectionKeys?.ranges) for (const r of sectionKeys.ranges) er.add(r);
  }

  if (ef.size > 0) params.set("enabled_filters", Array.from(ef).join(DELIMITER));
  else params.delete("enabled_filters");

  if (er.size > 0) params.set("enabled_ranges", Array.from(er).join(DELIMITER));
  else params.delete("enabled_ranges");

  const qs = params.toString();
  return `/products${qs ? `?${qs}` : ""}`;
}

/**
 * Build API-ready URLSearchParams from page searchParams.
 * Strips UI-only params, adds hitsSize/from.
 */
export function buildApiParams(
  searchParams: URLSearchParams,
  from: number = 0,
  hitsSize: number = 20
): URLSearchParams {
  const apiParams = new URLSearchParams();

  searchParams.forEach((value, key) => {
    if (!UI_ONLY_PARAMS.includes(key as (typeof UI_ONLY_PARAMS)[number])) {
      apiParams.set(key, value);
    }
  });

  apiParams.set("hitsSize", String(hitsSize));
  apiParams.set("from", String(from));

  return apiParams;
}

/**
 * Strip UI-only params for canonical URL
 */
export function buildCanonicalParams(searchParams: URLSearchParams): string {
  const canonical = new URLSearchParams();
  searchParams.forEach((value, key) => {
    if (
      !UI_ONLY_PARAMS.includes(key as (typeof UI_ONLY_PARAMS)[number]) &&
      key !== "enabled_filters" &&
      key !== "enabled_ranges"
    ) {
      canonical.set(key, value);
    }
  });
  const qs = canonical.toString();
  return qs ? `?${qs}` : "";
}
